/* eslint-disable @typescript-eslint/no-magic-numbers */
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  Collaboration,
  CollaborationAttachmentsDataService,
  CollaborationsDataService,
  CollaborationsGuardedDataService,
  CreateCollab,
  CreateMessage,
  CreateMessageAttachment,
  EmployeeStatus,
  EmployeeType,
  PatientDetails,
  Profile,
  ProfileSite,
  UpdateCollab,
} from '@ffd/api-client';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AudioInputSelectorModalComponent,
  AudioInputSelectorModalData,
  AudioInputSelectorModalResult,
  ConfigService,
  FileUploadService,
  NavigationService,
  queryParameter,
  TagEvent,
  TagPusherService,
} from '@libs/shared-services';
import {
  appActions,
  selectCurrentUserProfile,
  selectHasImagingSoftwareBySiteId,
  selectIsUserProviderAtSite,
  selectSelectedPatient,
  selectIsUserSiteOffline,
  selectUserSite,
  selectUserSiteId,
  selectWindowFocused,
  selectIsUserPrimaryStaffAtSite,
} from '@libs/store';
import { FfdComponentStore, isNotNull, routes, Loadable, DataState, filterWithRemaining } from '@libs/utils';
import { tapResponse, concatLatestFrom } from '@ngrx/operators';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, iif, map, mergeMap, Observable, of, switchMap, take, tap, zip } from 'rxjs';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { fullSeriesTypes, mapImagesToFullSeries, mapMessageAttachmentsToImagingGalleryItems } from '@libs/imaging';
import { Dialog } from '@angular/cdk/dialog';
import { ActiveTranscriptionService } from '@libs/speech-recognition';
import { ConfirmDialogResult } from '@libs/ui-lib';
import { TranslateService } from '@ngx-translate/core';
import { collaborationsActions } from '../../store';
import { CollaborationSidebarFilterUpdate, CollabSidebarElement } from '../collab-sidebar';
import { CollaborationEditorDialogResult } from '../collaboration-editor-dialog';
import { MessageVM, CollabMessageAttachment, CollaborationMessage } from '../message';
import { CategorizedMessageAttachmentVM, ImagingGalleryAttachment, MessageInputFullSeriesVM, MessageInputVM } from '../message-input';
import { getSortedMessagesAscending, mapCollaborations, mapCollaborationToChatHeader, mapMessages } from '../../utils';
import { CollaborationChatHeaderVM } from '../collaboration-chat-header/collaboration-chat-header.interface';
import { CollaborationDeleteInformationDialogComponent } from '../collaboration-delete-information-dialog/collaboration-delete-information-dialog.component';
import { CollaborationScreenRecordingService } from './collaboration-screen-recording.service';

interface SendMessageAttachment {
  createProps: CreateMessageAttachment;
  thumbnailUrl: string;
  sendingInProgressMessage: CollaborationMessage;
}

export interface MessageHistoryElement {
  message: string;
}

interface CollaborationState {
  collaborationListLimit: number;
  collaborations: Loadable<Collaboration[]>;
  noMoreCollaborations: boolean;
  selectedCollaboration: Loadable<Collaboration>;
  messagesAscending: CollaborationMessage[];
  newMessageMarkerBeforeMessageId?: string;
  noMoreMessages: boolean;
  patientId?: string;
  sites: ProfileSite[];
  messageListLimit: number;
  userId?: string;
  filterActive: boolean;
  searchTerm?: string;
  filterSiteIds?: string[];
  filterOpen: boolean;
  filterUnreadOnly?: boolean;
  filterDirectOnly?: boolean;
  filterTeamCollab?: boolean;
  filterClinicalCollab?: boolean;
  filterRecentlyDeleted?: boolean;
  hasUnsentMessages?: boolean;
  hasPendingMessages?: boolean;
  isPageActive?: boolean;
  isGenerating: boolean;
  hasDeletedCollabs: boolean;
  isUserSiteAdmin?: boolean;
}

export interface CollaborationViewModel {
  patientId?: string;
  sites: ProfileSite[];
  collaborations: Loadable<CollabSidebarElement[]>;
  currentUserIds: string[];
  selectedPatient: Loadable<PatientDetails>;
  selectedCollaboration: Loadable<CollabSidebarElement>;
  selectedSite?: ProfileSite;
  chatHeaderInfo?: CollaborationChatHeaderVM;
  messages?: MessageVM[];
  newMessageMarkerBeforeMessageId?: string;
  filters: {
    active: boolean;
    searchTerm?: string | null;
    siteIds?: string[] | null;
    open: boolean;
    unreadOnly?: boolean;
    directOnly?: boolean;
    teamCollab?: boolean;
    clinicalCollab?: boolean;
  };
  hasImagingSoftware: Loadable<boolean>;
  isGenerating: boolean;
  disableImageUpload: boolean;
  hasDeletedCollabs: boolean;
}

const initialState: CollaborationState = {
  collaborationListLimit: 20,
  collaborations: {
    data: [],
    state: DataState.Initial,
  },
  selectedCollaboration: {
    state: DataState.Initial,
    data: undefined,
  },
  noMoreCollaborations: false,
  messagesAscending: [],
  noMoreMessages: false,
  sites: [],
  messageListLimit: 20,
  filterActive: false,
  searchTerm: undefined,
  filterSiteIds: undefined,
  filterOpen: true,
  filterUnreadOnly: false,
  filterDirectOnly: false,
  hasUnsentMessages: false,
  hasPendingMessages: false,
  isGenerating: false,
  isPageActive: false,
  hasDeletedCollabs: false,
};

@Injectable({ providedIn: 'root' })
export class CollaborationScreenService extends FfdComponentStore<CollaborationState, CollaborationViewModel> {
  private readonly collaborationsGuardedDataService = inject(CollaborationsGuardedDataService);
  private readonly collaborationDataService = inject(CollaborationsDataService);
  private readonly collaborationAttachmentDataService = inject(CollaborationAttachmentsDataService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly configService: ConfigService = inject(ConfigService);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly screenRecordingService = inject(CollaborationScreenRecordingService);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly navigationService = inject(NavigationService);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly activeTranscriptionService = inject(ActiveTranscriptionService);
  private readonly translateService = inject(TranslateService);

  public allowErrorModalSignal = signal(true);

  public readonly recordingStatus = this.screenRecordingService.recordingStatus;
  public readonly mediaVolumeLevel = this.screenRecordingService.mediaVolumeLevel;
  public readonly micHasNoVolume = this.screenRecordingService.micHasNoVolume;
  public readonly hasPendingMessagesSignal = this.selectSignal((state) => state.hasPendingMessages);
  public readonly speechRecognitionStatus = this.activeTranscriptionService.status;
  public readonly SEARCH_TERM_MIN_LENGTH = 3;

  private readonly activeDeviceId = this.screenRecordingService.activeDeviceId;
  private readonly collabSiteId = computed(() => this.state().selectedCollaboration.data?.siteId ?? routes.showcaseSiteId);
  private readonly hasImagingSoftware = computed(() => this.store.selectSignal(selectHasImagingSoftwareBySiteId(this.collabSiteId()))());
  private readonly isProviderAtSite = computed(() => this.store.selectSignal(selectIsUserProviderAtSite)());
  private readonly isPrimaryStaffAtSite = computed(() => this.store.selectSignal(selectIsUserPrimaryStaffAtSite)());
  constructor() {
    super(initialState);

    effect(() => {
      // if we don't detect any volume from the microphone while recording, we show an error modal
      if (this.recordingStatus() === 'recording' && this.micHasNoVolume() && this.allowErrorModalSignal()) {
        this.openAudioInputErrorModal();
      }
    });
  }

  public override init(patientId?: string, allowCollabSelection = true, filters?: CollaborationSidebarFilterUpdate): void {
    this.patchState(initialState);
    this.setPatientId(patientId);
    this.getCollabIdFromUrl();
    this.getUser();
    this.getUserSiteAdmin();
    if (filters) {
      this.updateFilters({ filters, fetchCollabs: false, clearCollabs: true });
    }
    this.getAndAppendCollaborations({ selectFirstCollaboration: allowCollabSelection, append: true });
    this.listenToReceiveMessageEvent();
    this.listenToDeletedMessageEvent();
    this.listenToThumbnailReadyEvent();
    this.listenToAddToCollaborationEvent();
    this.listenToReadCollaborationEvent();
  }

  public activatePage(): void {
    this.patchState({ isPageActive: true });
  }

  public destroy(): void {
    this.patchState({ ...initialState, isPageActive: false });
  }

  public toggleRecord(): void {
    if (this.recordingStatus() === 'recording') {
      this.store.dispatch(collaborationsActions.screenRecordingStopRequested());

      return;
    }

    const selectedCollaboration = this.state().selectedCollaboration;
    const siteId = this.store.selectSignal(selectUserSiteId)();

    if (!selectedCollaboration || !siteId) {
      return;
    }
    if (selectedCollaboration.data?.id) {
      this.store.dispatch(
        collaborationsActions.screenRecordingRequested({
          collaborationId: selectedCollaboration.data.id,
          siteId,
          patientId: this.state().patientId,
        }),
      );
    }
  }

  public restartRecording(deviceId: string): void {
    this.activeTranscriptionService.selectMicrophone(deviceId);

    const selectedCollaboration = this.state().selectedCollaboration;
    const siteId = this.store.selectSignal(selectUserSiteId)();

    this.store.dispatch(collaborationsActions.screenRecordingStopRequested());
    setTimeout(() => {
      this.store.dispatch(
        collaborationsActions.screenRecordingRequested({
          collaborationId: selectedCollaboration?.data?.id ?? '',
          siteId: siteId ?? '',
        }),
      );
    }, 2000);
  }

  public openAudioInputErrorModal(): void {
    this.allowErrorModalSignal.set(false);

    this.dialog
      .open<AudioInputSelectorModalResult, AudioInputSelectorModalData, AudioInputSelectorModalComponent>(
        AudioInputSelectorModalComponent,
        {
          backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
          panelClass: ['w-[336px]'],
          disableClose: true,
          data: {
            activeDeviceId: this.activeDeviceId(),
            shouldDisplayRecordWithoutSoundItem: true,
          },
        },
      )
      .closed.subscribe((result) => {
        if (result && result.selectedDeviceId && result.selectedDeviceId !== this.activeDeviceId()) {
          //stop and start speech recognition
          this.restartRecording(result.selectedDeviceId);
        }
      });
  }

  protected override mapStateToViewModel(state: CollaborationState): CollaborationViewModel {
    const selectedCollaboration = state.selectedCollaboration.data;
    const selectedCollaborationState = state.selectedCollaboration.state;
    const selectedSite = this.store.selectSignal(selectUserSite);
    const selectedPatient = this.store.selectSignal(selectSelectedPatient)();
    const isSiteOffline = this.store.selectSignal(selectIsUserSiteOffline)();

    return {
      patientId: state.patientId,
      sites: state.sites.filter((site) => site.id !== routes.showcaseSiteId),
      collaborations: {
        data: mapCollaborations(state.collaborations.data ?? [], state.userId, state.isUserSiteAdmin, this.translateService),
        state: state.collaborations.state,
      },
      selectedPatient,
      currentUserIds: state.sites.map((site) => site.provider?.id ?? site.patient?.id).filter((id): id is string => !!id),
      chatHeaderInfo: mapCollaborationToChatHeader(selectedCollaboration, selectedPatient.data, state.userId),
      selectedCollaboration: {
        state: selectedCollaborationState,
        data: selectedCollaboration
          ? mapCollaborations([selectedCollaboration], state.userId, state.isUserSiteAdmin, this.translateService)[0]
          : undefined,
      },
      selectedSite: selectedSite(),
      messages: mapMessages(state.messagesAscending, selectedCollaboration, this.state().userId),
      newMessageMarkerBeforeMessageId: state.newMessageMarkerBeforeMessageId,
      filters: {
        active: state.filterActive,
        searchTerm: state.searchTerm,
        siteIds: state.filterSiteIds,
        open: state.filterOpen,
        unreadOnly: state.filterUnreadOnly,
      },
      hasImagingSoftware: this.hasImagingSoftware(),
      isGenerating: state.isGenerating,
      disableImageUpload: isSiteOffline && !!selectedCollaboration && selectedCollaboration.siteId === selectedSite()?.id,
      hasDeletedCollabs: state.hasDeletedCollabs,
    };
  }

  public readonly setPatientId = this.updater(
    (s, patientId: string | undefined): CollaborationState => ({
      ...s,
      patientId,
    }),
  );

  private readonly addNewMessage = this.updater(
    (s, message: CollaborationMessage): CollaborationState => ({
      ...s,
      messagesAscending: s.messagesAscending.some((m) => m.id === message.id)
        ? [...s.messagesAscending]
        : [...s.messagesAscending, message],
    }),
  );

  private readonly updateMessageWithThumbnail = this.updater(
    (
      s,
      data: {
        messageId: string;
        attachmentId: string;
        thumbnailUrl: string;
        toothPositionString?: string;
        date?: string;
        toothPositions?: number[];
      },
    ): CollaborationState => ({
      ...s,
      messagesAscending: s.messagesAscending.map((message) => {
        if (message.id === data.messageId) {
          return {
            ...message,
            attachments: message.attachments!.map((attachment) => {
              if (attachment.id === data.attachmentId) {
                return {
                  ...attachment,
                  thumbnailUrl: data.thumbnailUrl,
                };
              }

              return attachment;
            }),
          };
        }

        return message;
      }),
    }),
  );

  public readonly resetPendingMessages = this.updater(
    (state): CollaborationState => ({
      ...state,
      hasPendingMessages: false,
    }),
  );

  public readonly resetSelectedCollaboration = this.updater(
    (state): CollaborationState => ({
      ...state,
      selectedCollaboration: {
        state: DataState.Empty,
        data: undefined,
      },
    }),
  );

  public readonly selectCollaboration = this.effect(($: Observable<string>) =>
    $.pipe(
      tap((collaborationId) => {
        const selectedCollaborationSiteId = this.state().collaborations.data?.find((collab) => collab.id === collaborationId)?.siteId;
        if (this.isProviderAtSite() || this.isPrimaryStaffAtSite()) {
          this.store.dispatch(appActions.loadHasImagingSoftwareState({ siteId: selectedCollaborationSiteId }));
        }
      }),
      switchMap((collaborationId: string) => {
        this.patchState({
          selectedCollaboration: {
            state: DataState.Loading,
            data: this.state().selectedCollaboration.data,
          },
        });
        return this.collaborationsGuardedDataService.getCollaboration(collaborationId).pipe(
          tapResponse(
            (collaboration) => {
              this.patchState({
                selectedCollaboration: {
                  state: DataState.Fetched,
                  data: collaboration,
                },
                collaborations: {
                  data:
                    this.state().collaborations.data?.map((collab) =>
                      collab.id === collaboration.id
                        ? collaboration.deletedAfter
                          ? { ...collaboration }
                          : { ...collaboration, hasUnreadMessage: false }
                        : collab,
                    ) ?? [],
                  state: DataState.Fetched,
                },
                messagesAscending: [],
                noMoreMessages: false,
              });

              this.router.navigate([], { queryParams: { collaborationId: collaboration.id }, queryParamsHandling: 'merge' });

              this.getAndAppendMessages();
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly sendMessage = this.effect(($: Observable<MessageInputVM>) =>
    $.pipe(
      concatLatestFrom(() => [this.select((state) => state.selectedCollaboration.data)]),
      filter(([message, collab]) => !!collab && !!(message.text || message.attachments.allAttachments.length > 0)),
      tap(() => this.patchState({ hasPendingMessages: true })),
      // eslint-disable-next-line sonarjs/cognitive-complexity
      mergeMap(([message, collab]) => {
        //Uploading attachments may take a while, so we show the message as sent immediately
        const sendingInProgressMessage = this.createSendingInProgressMessage(message);
        this.addNewMessage(sendingInProgressMessage);

        const attachments = [...message.attachments.allAttachments]
          .filter((attachment) => !!attachment.file)
          .map((attachment, index) =>
            this.fileUploadService.uploadCollaborationAttachment(attachment.file!).pipe(
              filter((event): event is HttpResponse<{ fileId: string }> => event.type === HttpEventType.Response),
              map((uploadFile) => {
                if (!uploadFile.body) throw new Error('No body in uploadFile response');

                // Update the temp attachments with uploaded file ids, so that we can retry sending them if needed
                if (
                  sendingInProgressMessage.attachments &&
                  sendingInProgressMessage.attachments.length > 0 &&
                  sendingInProgressMessage.attachments[index]
                ) {
                  sendingInProgressMessage.attachments[index].id = uploadFile.body.fileId;
                }

                return {
                  createProps: {
                    category: attachment.category,
                    mediaType: attachment.mediaType,
                    fileId: uploadFile.body.fileId,
                    date: attachment.date,
                    toothPositions: attachment.toothPositions,
                    toothPositionString: attachment.toothPositionString,
                  } satisfies CreateMessageAttachment,
                  thumbnailUrl: attachment.url,
                  sendingInProgressMessage,
                } as SendMessageAttachment;
              }),
              catchError((error) => {
                console.error(error);
                if (sendingInProgressMessage) {
                  this.errorTempMessage(sendingInProgressMessage.id, sendingInProgressMessage.attachments, true);
                }
                return of();
              }),
            ),
          );

        return iif(() => !!message.attachments.fullSeries || message.attachments.uncategorized.length > 0, zip(attachments), of([])).pipe(
          mergeMap((attachments) => {
            return this.sendMessageToCloud(
              (collab as Collaboration).id!,
              { text: message.text, attachments: attachments.map((a) => a.createProps) },
              sendingInProgressMessage,
            );
          }),
        );
      }),
    ),
  );

  public readonly retrySendMessage = this.effect((retryMessageId$: Observable<string>) =>
    retryMessageId$.pipe(
      concatLatestFrom((messageId) => [this.select((state) => state.messagesAscending.find((m) => m.id === messageId))]),
      filter(([_, retryMessage]) => !!retryMessage),
      tap(([_, retryMessage]) => {
        if (retryMessage) {
          this.removeSendingInProgressMessage(retryMessage.id);
          const attachments = retryMessage.attachments ?? [];
          const mappedAttachments = attachments.map((attachment) => ({
            ...attachment,
            type: attachment.mediaType,
            category: attachment.category,
            url: attachment.url ?? '',
            file: attachment.file ?? new File([], ''),
          }));

          const [fullSeriesImages, uncategorized] = filterWithRemaining(
            mappedAttachments ?? [],
            (attachment) => fullSeriesTypes.includes(attachment.category) && !!attachment.toothPositions?.length,
          );

          const fullSeries =
            fullSeriesImages.length > 0
              ? (mapImagesToFullSeries<ImagingGalleryAttachment>(
                  mapMessageAttachmentsToImagingGalleryItems(fullSeriesImages) as ImagingGalleryAttachment[],
                ) as MessageInputFullSeriesVM)
              : undefined;

          this.sendMessage({
            text: retryMessage.text ?? '',
            attachments: {
              fullSeries,
              uncategorized,
              allAttachments: mappedAttachments,
            },
          });
        }
      }),
    ),
  );

  private createSendingInProgressMessage(message: { text: string; attachments: CategorizedMessageAttachmentVM }): CollaborationMessage {
    const attachments = message.attachments.allAttachments.filter((attachment) => !!attachment.file);

    return {
      id: `temp-${crypto.randomUUID()}`,
      sentAt: new Date().toISOString(),
      senderId: this.state().userId!,
      text: message.text,
      attachments: attachments.map((attachment) => {
        return {
          id: `temp-${crypto.randomUUID()}`,
          mediaType: attachment.mediaType,
          category: attachment.category,
          url: attachment.url,
          thumbnailUrl: attachment.url,
          date: attachment.date,
          toothPositions: attachment.toothPositions,
          toothPositionString: attachment.toothPositionString,
          file: attachment.file,
        };
      }),
      isSending: true,
    };
  }

  private sendMessageToCloud(collabId: string, createMessage: CreateMessage, sendingInProgressMessage: CollaborationMessage | null) {
    return this.collaborationsGuardedDataService.sendMessage(collabId, createMessage).pipe(
      tapResponse(
        (sentMessage) => {
          if (sendingInProgressMessage) {
            this.removeSendingInProgressMessage(sendingInProgressMessage.id);
            sentMessage.attachments = sendingInProgressMessage.attachments;
          }

          if (this.configService.config()?.useMockApi) {
            this.patchState({ messagesAscending: [] });
            this.getAndAppendMessages();
          } else {
            this.addNewMessage(sentMessage);
          }

          this.patchState({ hasPendingMessages: false });
        },
        (error) => {
          console.error(error);
          if (sendingInProgressMessage) {
            this.errorTempMessage(sendingInProgressMessage.id, sendingInProgressMessage.attachments);
          }
        },
      ),
    );
  }

  public readonly createCollaboration = this.effect(($: Observable<CollaborationEditorDialogResult>) =>
    $.pipe(
      concatLatestFrom(() => [this.store.select(selectUserSiteId)]),
      switchMap(([createCollabRequest, siteId]) => {
        const createCollab: CreateCollab = {
          siteId: createCollabRequest.siteId!,
          title: createCollabRequest.subject ?? '',
          participants: {
            providerIds:
              siteId === routes.showcaseSiteId
                ? []
                : createCollabRequest.employees
                    .filter((e) => !!e.pmsEmployeeId && e.participantRole === EmployeeType.PROVIDER)
                    .map(({ id }) => id),
            staffIds:
              routes.showcaseSiteId === siteId
                ? []
                : createCollabRequest.employees
                    .filter((e) => !!e.pmsEmployeeId && e.participantRole === EmployeeType.STAFF)
                    .map(({ id }) => id),
            emails: siteId === routes.showcaseSiteId ? createCollabRequest.emailParticipants.map(({ email }) => email!) : [],
            patientId: createCollabRequest.patient?.id,
          },
          patientSubjectId: createCollabRequest.linkedPatient?.id,
        };
        return this.collaborationDataService.createCollaboration(createCollab).pipe(
          tapResponse(
            (collaboration) => {
              if (createCollabRequest.navigateToCollaboration) {
                this.navigationService.navigate('allCollaborationsOnSite', false, [siteId!], { collaborationId: collaboration.id });
              }

              if (siteId === routes.showcaseSiteId) {
                this.tagPusherService.pushTag({
                  event: TagEvent.ShowcaseInvitationSent,
                });
              } else if (createCollabRequest.employees.some((employee) => employee.status === EmployeeStatus.NOT_INVITED)) {
                this.tagPusherService.pushTag({
                  event: TagEvent.CollaborationInvitationSent,
                });
              }

              this.getAndAppendCollaborations({ selectFirstCollaboration: true, append: true });
              this.selectCollaboration(collaboration.id);
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly updateCollaboration = this.effect(($: Observable<CollaborationEditorDialogResult>) =>
    $.pipe(
      concatLatestFrom(() => [this.select((state) => state.selectedCollaboration.data?.id), this.store.select(selectUserSiteId)]),
      filter((value): value is [CollaborationEditorDialogResult, string, string] => isNotNull(value[1])),
      switchMap(([dialogData, collabId, siteId]) => {
        const updateCollab: UpdateCollab = {
          title: dialogData.subject || '',
          participants: {
            providerIds: dialogData.employees.filter((e) => e.participantRole === EmployeeType.PROVIDER).map(({ id }) => id),
            staffIds: dialogData.employees.filter((e) => e.participantRole === EmployeeType.STAFF).map(({ id }) => id),
            emails:
              siteId === routes.showcaseSiteId ? dialogData.emailParticipants.map((emailParticipant) => emailParticipant.email ?? '') : [],
            patientId: dialogData.patient?.id,
          },
          patientSubjectId: dialogData.linkedPatient?.id,
        };

        return this.collaborationsGuardedDataService.updateCollaboration(collabId, updateCollab).pipe(
          tapResponse(
            () => {
              this.selectCollaboration(collabId);
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly unsendMessage = this.effect(($: Observable<{ collabId: string; messageId: string }>) =>
    $.pipe(
      switchMap(({ collabId, messageId }) => {
        return this.collaborationsGuardedDataService.deleteMessage(collabId, messageId).pipe(
          tapResponse(
            () => {
              this.patchState({
                messagesAscending: this.state().messagesAscending.filter((message) => message.id !== messageId),
                collaborations: {
                  data: [],
                  state: DataState.Loading,
                },
                noMoreCollaborations: false,
              });

              this.getAndAppendCollaborations({ selectFirstCollaboration: true, append: true });
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly markMessageAsUnread = this.effect(($: Observable<{ collabId: string; messageId: string }>) =>
    $.pipe(
      switchMap(({ collabId, messageId }) => {
        return this.collaborationsGuardedDataService.markMessageAsUnread(collabId, messageId).pipe(
          tapResponse(
            () => {
              const currentMessages = this.state().messagesAscending;
              const messageIndex = currentMessages.findIndex((m) => m.id === messageId);
              const lastReadMessageId = messageIndex > 0 ? currentMessages[messageIndex - 1].id : undefined;

              this.patchState({
                collaborations: {
                  data: [],
                  state: DataState.Loading,
                },
                noMoreCollaborations: false,
                // Set the marker to show before the message that was marked as unread
                newMessageMarkerBeforeMessageId: messageId,
                // Update the selected collaboration to reflect unread status
                selectedCollaboration: {
                  state: DataState.Fetched,
                  data: this.state().selectedCollaboration.data
                    ? {
                        ...this.state().selectedCollaboration.data!,
                        hasUnreadMessage: true,
                        lastReadMessageId,
                      }
                    : undefined,
                },
              });

              // Only reload collaborations list, don't re-select the current collaboration
              // to avoid triggering the auto-mark-as-read logic in getAndAppendMessages
              this.getAndAppendCollaborations({ selectFirstCollaboration: true, append: true });
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly getAndAppendMessages = this.effect(($) =>
    $.pipe(
      concatLatestFrom(() => [
        this.select((state) => state.selectedCollaboration.data),
        this.select((state) => state.messageListLimit),
        this.select((state) => state.messagesAscending),
        this.select((state) => state.noMoreMessages),
      ]),
      filter(([_, collab, __, ___, noMoreMessages]) => !!collab && !noMoreMessages),
      switchMap(([_, collab, limit, currentMessages]) => {
        const collaborationId = (collab as Collaboration).id!;
        const firstBatchLoaded = currentMessages.length === 0;

        return this.collaborationsGuardedDataService.getMessagesInCollaboration(collaborationId, limit, currentMessages[0]?.id).pipe(
          tapResponse(
            (newMessages) => {
              const mergedSortedMessages = getSortedMessagesAscending([...newMessages.list, ...this.state().messagesAscending]);
              const lastReadMessageIndex = mergedSortedMessages.findIndex((m) => m.id === collab?.lastReadMessageId);
              const firstUnreadMessageId = lastReadMessageIndex > -1 ? mergedSortedMessages[lastReadMessageIndex + 1]?.id : undefined;

              this.patchState({
                messagesAscending: mergedSortedMessages,
                noMoreMessages: newMessages.list.length < limit,
                newMessageMarkerBeforeMessageId: collab?.hasUnreadMessage ? firstUnreadMessageId : undefined,
              });

              if (firstBatchLoaded && newMessages.list.length > 0 && collab?.hasUnreadMessage) {
                this.markMessageAsRead({ collaborationId, messageId: mergedSortedMessages.at(-1)!.id });
              }
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly markMessageAsRead = this.effect(($: Observable<{ collaborationId: string; messageId: string }>) =>
    $.pipe(
      concatLatestFrom(() => [
        this.store.select(selectWindowFocused),
        this.select((state) => !!state.selectedCollaboration.data?.deletedAfter),
      ]),
      filter(([{ collaborationId, messageId }, windowFocused, isSoftDeletedCollab]) => !isSoftDeletedCollab),
      switchMap(([{ collaborationId, messageId }, windowFocused]) =>
        iif(
          () => windowFocused,
          of(true),
          this.actions$.pipe(
            ofType(appActions.windowFocusedChanged),
            filter(({ focused }) => focused),
            take(1),
          ),
        ).pipe(
          switchMap(() =>
            this.collaborationsGuardedDataService.markMessageAsRead(collaborationId, messageId).pipe(
              tapResponse(
                () => {},
                (error) => {
                  console.error(error);
                },
              ),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly getAndAppendCollaborations = this.effect((props$: Observable<{ selectFirstCollaboration: boolean; append: boolean }>) =>
    props$.pipe(
      concatLatestFrom(() => [
        this.select((state) => state.noMoreCollaborations),
        this.select((state) => state.collaborationListLimit),
        this.select((state) => state.searchTerm).pipe(
          // eslint-disable-next-line @ngrx/avoid-mapping-component-store-selectors
          map((searchTerm) => (searchTerm && searchTerm.length >= this.SEARCH_TERM_MIN_LENGTH ? searchTerm : undefined)),
        ),
        this.select((state) => state.filterUnreadOnly),
        this.select((state) => state.filterDirectOnly),
        this.select((state) => state.filterTeamCollab),
        this.select((state) => state.filterClinicalCollab),
        this.select((state) => state.filterRecentlyDeleted),
        this.select((state) => state.patientId),
        this.store.select(selectUserSiteId),
        this.select((state) => state.filterSiteIds),
        this.select((state) => state.sites),
      ]),
      filter(([, noMoreCollaborations]) => !noMoreCollaborations),
      switchMap(
        ([
          { selectFirstCollaboration, append },
          __,
          limit,
          searchTerm,
          unreadOnly,
          directOnly,
          teamCollab,
          clinicalCollab,
          recentlyDeleted,
          patientId,
          siteId,
          filterSiteIds,
          allSites,
          // eslint-disable-next-line sonarjs/cognitive-complexity
        ]) => {
          let siteIds: string[] = [];

          if (patientId && siteId) {
            //patient context
            siteIds = [siteId];
          } else {
            //showcase environment
            if (siteId === routes.showcaseSiteId) {
              //in case of showcase the site filter is hidden, so don't care about that here
              siteIds = [routes.showcaseSiteId];
            } else {
              // global collab page
              siteIds =
                filterSiteIds && filterSiteIds.length > 0
                  ? filterSiteIds
                  : allSites.filter((site) => site.id !== routes.showcaseSiteId).map((s) => s.id);
            }
          }

          const offset = append ? this.state().collaborations.data?.length ?? 0 : 0;

          this.patchState({
            collaborations: {
              data: this.state().collaborations.data,
              state: DataState.Loading,
            },
          });

          // if a collab has patient, it is a clinical collab
          let hasPatient: boolean | undefined;
          if (clinicalCollab && !teamCollab) {
            hasPatient = true;
          } else if (!clinicalCollab && teamCollab) {
            hasPatient = false;
          }

          //this is a temporary solution, later we make these 2 filters as toggle buttons, so that the user can select both
          if (clinicalCollab && teamCollab) {
            this.patchState({
              collaborations: {
                data: [],
                state: DataState.Fetched,
              },
            });
            return of();
          }

          return this.collaborationDataService
            .getCollaborations(limit, offset, searchTerm, hasPatient, unreadOnly, patientId, siteIds, directOnly, recentlyDeleted)
            .pipe(
              tapResponse(
                (collaborations) => {
                  this.patchState({
                    collaborations: {
                      data: [...(append ? this.state().collaborations.data ?? [] : []), ...collaborations.list],
                      state: DataState.Fetched,
                    },
                    noMoreCollaborations: collaborations.list.length < limit,
                    hasDeletedCollabs: collaborations.hasDeletedCollabs,
                  });
                  const hasCollaborations = collaborations.list.length > 0;
                  const isLoadingSelectedCollaboration = this.state().selectedCollaboration.state === DataState.Loading;
                  const hasSelectedCollaboration = !!this.state().selectedCollaboration.data;
                  const isSiteIdDifferent = siteId !== this.state().selectedCollaboration.data?.siteId;
                  const isPageActive = this.state().isPageActive;
                  if (
                    selectFirstCollaboration &&
                    !hasSelectedCollaboration &&
                    !isLoadingSelectedCollaboration &&
                    isSiteIdDifferent &&
                    hasCollaborations &&
                    isPageActive
                  ) {
                    this.selectCollaboration(collaborations.list?.[0]?.id);
                  } else {
                    this.patchState({
                      selectedCollaboration: {
                        state: DataState.Fetched,
                        data: this.state().selectedCollaboration.data,
                      },
                    });
                  }
                },

                (error) => {
                  console.error(error);
                },
              ),
            );
        },
      ),
    ),
  );

  private readonly getCollabIdFromUrl = this.effect(($) =>
    $.pipe(
      switchMap(() => this.activatedRoute.queryParamMap),
      map((parameters) => {
        return parameters.get(queryParameter('collaborationId'));
      }),
      filter((collabId): collabId is string => !!collabId),
      filter((collabId) => collabId !== this.state().selectedCollaboration.data?.id),
      tap((collabId: string) => {
        this.selectCollaboration(collabId);
      }),
    ),
  );

  private readonly getUser = this.effect(($) =>
    $.pipe(
      switchMap(() => {
        return this.store.select(selectCurrentUserProfile).pipe(
          filter((profile): profile is Profile => !!profile),
          tap((profile) => {
            this.patchState({
              userId: profile.id,
              sites: profile.sites,
            });
          }),
        );
      }),
    ),
  );

  private readonly getUserSiteAdmin = this.effect(($) =>
    $.pipe(
      switchMap(() => {
        return this.store.select(selectUserSite).pipe(
          filter((site): site is ProfileSite => !!site),
          tap((site) => {
            this.patchState({
              isUserSiteAdmin: !!site.provider?.isAdmin,
            });
          }),
        );
      }),
    ),
  );

  private readonly listenToReceiveMessageEvent = this.effect(($) =>
    $.pipe(
      switchMap(() => this.actions$.pipe(ofType(collaborationsActions.wsReceiveMessage))),
      concatLatestFrom(() => [
        this.store.select(selectWindowFocused),
        this.select((state) => state.userId),
        this.store.select(selectUserSiteId),
      ]),
      tap(([{ data }, windowFocused, currentUserId, currentSiteId]) => {
        const messageReceivedInSelectedCollaboration = data.collaborationId === this.state().selectedCollaboration.data?.id;

        const messageReceivedInShowcase = data.siteId === routes.showcaseSiteId;
        const showcaseIsSelected = currentSiteId === routes.showcaseSiteId;
        const shouldAppendCollab = showcaseIsSelected === messageReceivedInShowcase;

        if (!messageReceivedInSelectedCollaboration && shouldAppendCollab) {
          this.addCollabToList(data.collaborationId);
        }

        const messageSentByCurrentUser = data.message.senderId === currentUserId;
        const collaborationHasUnreadMessage = !messageSentByCurrentUser && (!windowFocused || !messageReceivedInSelectedCollaboration);

        this.patchState({
          collaborations: {
            data: this.state().collaborations.data?.map((collaboration) => {
              if (collaboration.id === data.collaborationId) {
                return {
                  ...collaboration,
                  hasUnreadMessage: collaborationHasUnreadMessage,
                  lastMessage: data.message,
                };
              }

              return collaboration;
            }),
            state: DataState.Fetched,
          },
          ...(messageReceivedInSelectedCollaboration && {
            selectedCollaboration: {
              state: DataState.Fetched,
              data: {
                ...this.state().selectedCollaboration.data!,
                lastReadMessageId:
                  data.message.senderId === this.state().userId || windowFocused
                    ? data.message.id
                    : this.state().selectedCollaboration.data!.lastReadMessageId,
              },
            },
          }),
        });

        if (messageReceivedInSelectedCollaboration && !messageSentByCurrentUser) {
          this.addNewMessage(data.message);
          this.markMessageAsRead({ collaborationId: data.collaborationId, messageId: data.message.id });
        }

        if (collaborationHasUnreadMessage) {
          this.store.dispatch(
            collaborationsActions.collaborationBecameUnread({ collaborationId: data.collaborationId, siteId: data.siteId }),
          );
        }

        if (
          messageReceivedInSelectedCollaboration &&
          !this.state().newMessageMarkerBeforeMessageId &&
          !windowFocused &&
          !messageSentByCurrentUser
        ) {
          this.patchState({ newMessageMarkerBeforeMessageId: data.message.id });
        }
      }),
    ),
  );

  private readonly addCollabToList = this.effect(($: Observable<string>) =>
    $.pipe(
      concatLatestFrom(() => [this.select((state) => state.collaborations)]),
      concatMap(([collabId, collaborations]) =>
        iif(
          () => collaborations.data?.some((collab) => collab.id === collabId) ?? false,
          of(collaborations.data?.find((collab) => collab.id === collabId)),
          this.collaborationsGuardedDataService.getCollaboration(collabId),
        ),
      ),
      tap((collab) => {
        this.patchState({
          collaborations: {
            data: [
              collab as Collaboration,
              ...(this.state().collaborations.data?.filter((c) => c.id !== (collab as Collaboration).id) ?? []),
            ],
            state: DataState.Fetched,
          },
        });
      }),
    ),
  );

  private readonly listenToDeletedMessageEvent = this.effect(($) =>
    $.pipe(
      switchMap(() => this.actions$.pipe(ofType(collaborationsActions.wsDeleteMessage))),
      tap(({ data }) => {
        this.patchState({
          messagesAscending: this.state().messagesAscending.filter((message) => message.id !== data.messageId),
        });
      }),
    ),
  );

  private readonly listenToThumbnailReadyEvent = this.effect(($) =>
    $.pipe(
      switchMap(() => this.actions$.pipe(ofType(collaborationsActions.wsThumbnailReady))),
      concatLatestFrom(({ data }) => [this.select((state) => state.messagesAscending?.find((m) => m.id === data.messageId))]),
      filter(([, message]) => !!message),
      concatMap(([{ data }]) =>
        this.collaborationAttachmentDataService.getThumbnailDownloadLinkForAttachment(data.attachmentId).pipe(
          tapResponse(
            (thumbnailResponse) => {
              this.updateMessageWithThumbnail({
                messageId: data.messageId,
                attachmentId: data.attachmentId,
                thumbnailUrl: thumbnailResponse.url!,
              });
            },
            (error) => {
              console.error(error);
            },
          ),
        ),
      ),
    ),
  );

  private readonly listenToAddToCollaborationEvent = this.effect(($) =>
    $.pipe(
      switchMap(() => this.actions$.pipe(ofType(collaborationsActions.wsAddToCollaboration))),
      concatLatestFrom(() => [this.store.select(selectUserSiteId)]),
      filter(([{ data }, currentSiteId]) => {
        const messageReceivedInShowcase = data.siteId === routes.showcaseSiteId;
        const showcaseIsSelected = currentSiteId === routes.showcaseSiteId;

        return showcaseIsSelected === messageReceivedInShowcase;
      }),
      switchMap(([{ data }]) => {
        return this.collaborationsGuardedDataService.getCollaboration(data.collaborationId).pipe(
          tapResponse(
            (collaboration) => {
              if (!this.state().collaborations.data?.some((collab) => collab.id === collaboration.id)) {
                this.patchState({
                  collaborations: {
                    data: [...(this.state().collaborations.data ?? []), collaboration],
                    state: DataState.Fetched,
                  },
                });
              }
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  private readonly listenToReadCollaborationEvent = this.effect(($) =>
    $.pipe(
      switchMap(() => this.actions$.pipe(ofType(collaborationsActions.wsReadCollaboration))),
      tap(({ data }) => {
        this.patchState({
          collaborations: {
            data: this.state().collaborations.data?.map((collaboration) => {
              if (collaboration.id === data.collaborationId) {
                return {
                  ...collaboration,
                  hasUnreadMessage: false,
                };
              }

              return collaboration;
            }),
            state: DataState.Fetched,
          },
        });
      }),
    ),
  );

  public clearFilters(): void {
    this.patchState({
      filterActive: false,
      searchTerm: undefined,
      filterSiteIds: undefined,
      filterUnreadOnly: false,
      filterDirectOnly: false,
      messageListLimit: 20,
      collaborations: {
        data: [],
        state: DataState.Loading,
      },
      noMoreCollaborations: false,
    });

    this.getAndAppendCollaborations({ selectFirstCollaboration: true, append: true });
  }

  public updateFilters({
    filters,
    fetchCollabs,
    clearCollabs,
  }: {
    filters: CollaborationSidebarFilterUpdate;
    fetchCollabs: boolean;
    clearCollabs: boolean;
  }): void {
    const { searchTerm, selectedSiteIds, unreadOnly, directOnly, teamCollab, clinicalCollab, recentlyDeleted } = filters;

    // if the filtering happens on the home page sidebar, we do not clear the collabs to prevent flickering caused by the short loading state
    if (clearCollabs) {
      this.resetSelectedCollaboration();
      this.patchState({
        collaborations: {
          data: [],
          state: DataState.Loading,
        },
      });
    }

    this.patchState({
      searchTerm: searchTerm,
      filterSiteIds: selectedSiteIds,
      filterUnreadOnly: unreadOnly,
      filterDirectOnly: directOnly,
      filterTeamCollab: teamCollab,
      filterClinicalCollab: clinicalCollab,
      filterRecentlyDeleted: recentlyDeleted,
      noMoreCollaborations: false,
    });

    if (fetchCollabs) {
      this.getAndAppendCollaborations({ selectFirstCollaboration: true, append: clearCollabs });
    }
  }

  public toggleFilters(): void {
    this.patchState({ filterOpen: !this.state().filterOpen });
  }

  public readonly leaveCollaboration = this.effect(($: Observable<{ collabId: string }>) =>
    $.pipe(
      switchMap(({ collabId }) => {
        return this.collaborationsGuardedDataService.leaveCollaboration(collabId).pipe(
          tapResponse(
            () => {
              // the collaboration that has been left, needs to be removed from the list
              // noMoreCollaborations needs to be set to false, otherwise the user won't be able to load more collaborations
              this.patchState({
                noMoreCollaborations: false,
              });
              // reload collaborations
              this.getAndAppendCollaborations({ selectFirstCollaboration: false, append: false });

              // reset selection
              this.resetSelectedCollaboration();

              // filter out the left collaboration from the list
              const collabList = this.state().collaborations.data?.filter((collaboration) => collaboration.id !== collabId);

              // select the first remaining collaboration if exists
              if (collabList && collabList.length > 0) {
                this.selectCollaboration(collabList?.[0].id);
              }
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly recoverCollaboration = this.effect(($: Observable<{ collabId: string }>) =>
    $.pipe(
      switchMap(({ collabId }) => {
        return this.collaborationsGuardedDataService.recoverCollaboration(collabId).pipe(
          tapResponse(
            () => {
              // the collaboration that has been recovered, needs to be removed from the list
              // noMoreCollaborations needs to be set to false, otherwise the user won't be able to load more collaborations
              this.patchState({
                noMoreCollaborations: false,
              });
              // reload collaborations
              this.getAndAppendCollaborations({ selectFirstCollaboration: false, append: false });

              // if the selected collaboration is the one that was recovered, it needs to be reselected to update the collab
              if (this.state().selectedCollaboration.data?.id === collabId) {
                this.selectCollaboration(collabId);
              }
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly deleteCollaboration = this.effect(($: Observable<{ collabId: string }>) =>
    $.pipe(
      switchMap(({ collabId }) => {
        return this.collaborationsGuardedDataService.softDeleteCollaboration(collabId).pipe(
          tapResponse(
            () => {
              // the collaboration that has been soft deleted, needs to be removed from the list
              // noMoreCollaborations needs to be set to false, otherwise the user won't be able to load more collaborations
              this.patchState({
                noMoreCollaborations: false,
              });
              // reload collaborations
              this.getAndAppendCollaborations({ selectFirstCollaboration: false, append: false });

              // if the selected collaboration is the one that was soft deleted, it needs to be reselected to update the collab
              if (this.state().selectedCollaboration.data?.id === collabId) {
                this.selectCollaboration(collabId);
              }

              // show success dialog with information about soft deleted collaboration only on first delete
              const localStorageKey = 'ffd_collab_delete_info_shown';
              try {
                const alreadyShown = localStorage.getItem(localStorageKey);
                if (!alreadyShown) {
                  this.dialog.open<ConfirmDialogResult>(CollaborationDeleteInformationDialogComponent, {
                    backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light', 'collab-delete-info-backdrop'],
                  });
                  try {
                    localStorage.setItem(localStorageKey, '1');
                  } catch {
                    // ignore write errors
                  }
                }
              } catch {
                // if localStorage is not available, fall back to showing the dialog
                this.dialog.open<ConfirmDialogResult>(CollaborationDeleteInformationDialogComponent);
              }
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly permanentDeleteCollaboration = this.effect(($: Observable<{ collabId: string }>) =>
    $.pipe(
      switchMap(({ collabId }) => {
        return this.collaborationsGuardedDataService.deleteCollaboration(collabId).pipe(
          tapResponse(
            () => {
              // the collaboration that has been deleted, needs to be removed from the list
              // noMoreCollaborations needs to be set to false, otherwise the user won't be able to load more collaborations
              this.patchState({
                noMoreCollaborations: false,
              });
              // reload collaborations
              this.getAndAppendCollaborations({ selectFirstCollaboration: false, append: false });
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  private removeSendingInProgressMessage(sendingInProgressMessageId: string) {
    this.patchState((state) => {
      const messages = state.messagesAscending.filter((message) => message.id !== sendingInProgressMessageId);
      return { messagesAscending: messages };
    });
  }

  private errorTempMessage(tempMessageId: string, attachments: CollabMessageAttachment[] | undefined, isUploadFailed?: boolean) {
    this.patchState((state) => {
      const messages = state.messagesAscending.map((message) => {
        if (message.id === tempMessageId) {
          if (isUploadFailed) {
            attachments = attachments?.map((attachment) => ({ ...attachment, id: attachment.id.replace('temp', 'error') }));
          }
          return { ...message, id: message.id.replace('temp', 'error'), attachments: attachments, isSending: false, isFailed: true };
        }
        return message;
      });
      return { messagesAscending: messages };
    });
  }
}
